import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ShieldCheck, ArrowLeft, ExternalLink, Copy, Check, Phone, Mail, MessageCircle,
  Instagram, Facebook, Globe, Save, Plus,
} from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { fetchSalesRecord, patchSalesRecord, changeSalesStage, addSalesActivity, changeSiteStatus } from "@/lib/api";
import { getPlanPrice } from "@/data/content";

const STAGE_LABEL = {
  not_contacted: "Not Contacted", contacted: "Contacted", delivered_or_seen: "Delivered / Seen",
  concept_viewed: "Concept Viewed", replied: "Replied", conversation: "Conversation",
  proposal_sent: "Proposal Sent", won: "Won", lost: "Lost",
};
const STAGE_ORDER = Object.keys(STAGE_LABEL);

const CHANNEL_LABEL = {
  messenger: "Messenger", facebook: "Facebook", instagram: "Instagram", email: "Email",
  whatsapp: "WhatsApp", phone: "Phone", sms: "SMS", linkedin: "LinkedIn", in_person: "In Person", other: "Other",
};

// Derived from the same content.js pricing source every other page uses — never a
// second, independently hardcoded price list that can drift out of sync (§5-6).
const PLAN_LABEL = {
  ownership: `Ownership — ${getPlanPrice("ownership", "ph").price} one-time`,
  assisted_setup_launch: `Assisted Setup & Launch — ${getPlanPrice("assisted_setup_launch", "ph").price} one-time`,
  maintenance: `Maintenance — ${getPlanPrice("maintenance", "ph").price}/mo`,
  responsibility: `Responsibility — ${getPlanPrice("responsibility", "ph").price}/mo`,
  priority: `Priority & Reputation — ${getPlanPrice("priority", "ph").price}/mo`,
};

const OBJECTION_LABEL = {
  price: "Price", monthly_subscription: "Monthly subscription", prefers_one_time: "Prefers one-time",
  already_has_provider: "Already has a provider", already_has_website: "Already has a website",
  does_not_need_website: "Doesn't need a website", not_interested: "Not interested",
  needs_partner_approval: "Needs partner approval", needs_management_approval: "Needs management approval",
  timing: "Timing", budget: "Budget", trust: "Trust", needs_more_information: "Needs more information", other: "Other",
};

const LOST_REASON_LABEL = {
  explicit_rejection: "Explicit rejection", price: "Price", subscription_objection: "Subscription objection",
  chose_competitor: "Chose a competitor", existing_provider: "Existing provider", management_declined: "Management declined",
  not_priority: "Not a priority", business_closed: "Business closed", wrong_contact: "Wrong contact",
  unable_to_reach_decision_maker: "Unable to reach decision-maker", other: "Other",
};

const ACTIVITY_TYPE_LABEL = {
  message_sent: "Message sent", call_attempt: "Call attempt", call_completed: "Call completed",
  in_person_visit: "In-person visit", response_received: "Response received", proposal_sent: "Proposal sent",
  follow_up: "Follow-up", note: "Note",
};

function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
    >
      {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />} {copied ? "Copied" : label}
    </button>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{label}</label>{children}</div>;
}

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm";

export default function AdminSalesDetail() {
  const { slug } = useParams();
  const { token, email, checking, isLoggedIn, revealAdminUi, logout } = useAdminAuth();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState("");
  const [stageModal, setStageModal] = useState(null); // { newStage }
  const [lostReason, setLostReason] = useState("");
  const [lostReasonText, setLostReasonText] = useState("");
  const [linkWebsiteStatus, setLinkWebsiteStatus] = useState(true);

  const [form, setForm] = useState(null);
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpNote, setFollowUpNote] = useState("");
  const [objectionCategory, setObjectionCategory] = useState("");
  const [objectionText, setObjectionText] = useState("");

  const [activityForm, setActivityForm] = useState({ channel: "", direction: "outbound", activity_type: "message_sent", summary: "", message_text: "", result: "" });

  const load = () => {
    if (!token) return;
    fetchSalesRecord(token, slug)
      .then((data) => {
        setRecord(data);
        setForm({
          primary_contact_channel: data.site.primary_contact_channel || "",
          primary_contact_value: data.site.primary_contact_value || "",
          decision_maker_name: data.site.decision_maker_name || "",
          decision_maker_role: data.site.decision_maker_role || "",
          decision_maker_verified: Boolean(data.site.decision_maker_verified),
          recommended_plan: data.site.recommended_plan || "",
          selected_plan: data.site.selected_plan || "",
          sales_priority: data.site.sales_priority || "",
          sales_hook: data.site.sales_hook || "",
          outreach_wave: data.site.outreach_wave || "",
          outreach_initial: data.site.outreach_initial || "",
          outreach_followup_1: data.site.outreach_followup_1 || "",
          outreach_followup_2: data.site.outreach_followup_2 || "",
          sales_notes: data.site.sales_notes || "",
        });
        setFollowUpDate(data.site.next_follow_up_at || "");
        setFollowUpNote(data.site.follow_up_note || "");
        setObjectionCategory(data.site.objection_category || "");
        setObjectionText(data.site.objection_text || "");
      })
      .catch((err) => setError(err.response?.data?.detail || "Could not load this prospect's sales record."));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, slug]);

  const conceptUrl = useMemo(() => {
    if (!record?.site?.concept_url) return "";
    const path = record.site.concept_url;
    return path.startsWith("http") ? path : `${window.location.origin}${path}`;
  }, [record]);

  const withLink = (text) => (text || "").split("[concept link]").join(conceptUrl || "[concept link]");

  const saveFields = async (fields, successMessage = "Saved.") => {
    setSaving(true);
    setSaveNote("");
    try {
      await patchSalesRecord(token, slug, fields);
      setSaveNote(successMessage);
      load();
    } catch (err) {
      setSaveNote(err.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveNote(""), 2500);
    }
  };

  const handleStageClick = (newStage) => {
    if (newStage === record.site.sales_stage) return;
    if (newStage === "lost") {
      setLostReason(""); setLostReasonText(""); setLinkWebsiteStatus(false);
      setStageModal({ newStage: "lost" });
    } else if (newStage === "won") {
      setLinkWebsiteStatus(false);
      setStageModal({ newStage: "won" });
    } else {
      setStageModal({ newStage });
    }
  };

  const confirmStageChange = async () => {
    const target = stageModal.newStage;
    if (target === "lost" && !lostReason) { setSaveNote("A lost reason is required."); return; }
    setSaving(true);
    try {
      await changeSalesStage(token, slug, target, null, target === "lost" ? lostReason : undefined, target === "lost" ? lostReasonText : undefined);
      if (target === "won" && linkWebsiteStatus && record.site.status === "offline_concept") {
        await changeSiteStatus(token, slug, "proposal_accepted", "Marked via Sales Pipeline: prospect won.", undefined);
      } else if (target === "lost" && linkWebsiteStatus && record.site.status !== "rejected") {
        await changeSiteStatus(token, slug, "rejected", "Marked via Sales Pipeline: prospect lost.", lostReason);
      }
      setStageModal(null);
      load();
    } catch (err) {
      setSaveNote(err.response?.data?.detail || "Could not change sales stage.");
    } finally {
      setSaving(false);
    }
  };

  const applyFollowUpShortcut = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    setFollowUpDate(d.toISOString().slice(0, 10));
  };

  const submitActivity = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await addSalesActivity(token, slug, {
        channel: activityForm.channel || null,
        direction: activityForm.direction,
        activity_type: activityForm.activity_type,
        summary: activityForm.summary || null,
        message_text: activityForm.message_text || null,
        result: activityForm.result || null,
      });
      setActivityForm({ channel: "", direction: "outbound", activity_type: "message_sent", summary: "", message_text: "", result: "" });
      load();
    } catch (err) {
      setSaveNote(err.response?.data?.detail || "Could not log this activity.");
    } finally {
      setSaving(false);
    }
  };

  if (checking) return null;
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;
  if (error) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-rose-600">{error}</div>;
  if (!isLoggedIn) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-slate-500">DEBUG — Admin UI revealed, but this page needs a real login to load real data.</div>;
  if (!record || !form) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-slate-500">Loading…</div>;

  const site = record.site;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900">
      <div className="mx-auto max-w-4xl">
        <Link to="/admin/sales" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} /> Back to Sales Pipeline</Link>
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 text-emerald-700"><ShieldCheck size={18} /><span className="text-xs font-bold uppercase tracking-[0.16em]">Logged in as {email}</span></div>
          <button type="button" onClick={logout} className="text-sm text-slate-500 hover:text-slate-800">Log out</button>
        </div>

        <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold">{site.business_name}</h1>
            <p className="mt-1 text-sm text-slate-500">
              Website status: <Link to={`/admin/sites/${slug}`} className="underline hover:text-emerald-700">{site.status}</Link>
              {" · "}<a href={site.concept_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-700 inline-flex items-center gap-1">View concept <ExternalLink size={11} /></a>
            </p>
          </div>
        </div>

        {/* Stage selector */}
        <div className="mt-5 flex flex-wrap gap-2">
          {STAGE_ORDER.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => handleStageClick(s)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border ${site.sales_stage === s ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
            >
              {STAGE_LABEL[s]}
            </button>
          ))}
        </div>
        {site.sales_stage === "lost" && site.lost_reason && (
          <p className="mt-2 text-xs text-rose-600">Lost reason: {LOST_REASON_LABEL[site.lost_reason] || site.lost_reason}{site.lost_reason_text ? ` — "${site.lost_reason_text}"` : ""}</p>
        )}

        {saveNote && <p className="mt-3 text-sm text-emerald-700">{saveNote}</p>}

        {/* Won/Lost confirmation modal */}
        {stageModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h3 className="font-semibold text-lg">
                {stageModal.newStage === "won" && "Mark this prospect Won?"}
                {stageModal.newStage === "lost" && "Mark this prospect Lost"}
                {stageModal.newStage !== "won" && stageModal.newStage !== "lost" && `Move to "${STAGE_LABEL[stageModal.newStage]}"?`}
              </h3>

              {stageModal.newStage === "lost" && (
                <div className="mt-4 space-y-3">
                  <Field label="Lost reason (required)">
                    <select value={lostReason} onChange={(e) => setLostReason(e.target.value)} className={inputCls}>
                      <option value="">Select a reason…</option>
                      {Object.entries(LOST_REASON_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="Exact prospect response (optional)">
                    <textarea value={lostReasonText} onChange={(e) => setLostReasonText(e.target.value)} rows={3} className={inputCls} placeholder="Preserve their wording verbatim…" />
                  </Field>
                  {site.status !== "rejected" && (
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={linkWebsiteStatus} onChange={(e) => setLinkWebsiteStatus(e.target.checked)} />
                      Also mark website status as Rejected
                    </label>
                  )}
                </div>
              )}

              {stageModal.newStage === "won" && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm text-slate-600">This prospect has agreed to proceed. Do you also want to mark the website Proposal Accepted?</p>
                  {site.status === "offline_concept" ? (
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={linkWebsiteStatus} onChange={(e) => setLinkWebsiteStatus(e.target.checked)} />
                      Yes — mark website Proposal Accepted
                    </label>
                  ) : (
                    <p className="text-xs text-slate-400">Website status is already "{site.status}" — no change offered.</p>
                  )}
                </div>
              )}

              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setStageModal(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
                <button type="button" disabled={saving} onClick={confirmStageChange} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">Confirm</button>
              </div>
            </div>
          </div>
        )}

        {/* Decision-maker + contact */}
        <section className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold">Decision-Maker &amp; Contact</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Decision-maker name">
              <input value={form.decision_maker_name} onChange={(e) => setForm({ ...form, decision_maker_name: e.target.value })} className={inputCls} placeholder="Unknown" />
            </Field>
            <Field label="Role">
              <input value={form.decision_maker_role} onChange={(e) => setForm({ ...form, decision_maker_role: e.target.value })} className={inputCls} placeholder="Owner / Dentist" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
              <input type="checkbox" checked={form.decision_maker_verified} onChange={(e) => setForm({ ...form, decision_maker_verified: e.target.checked })} /> Verified
            </label>
            <Field label="Primary contact channel">
              <select value={form.primary_contact_channel} onChange={(e) => setForm({ ...form, primary_contact_channel: e.target.value })} className={inputCls}>
                <option value="">Not set</option>
                {Object.entries(CHANNEL_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Primary contact value">
              <input value={form.primary_contact_value} onChange={(e) => setForm({ ...form, primary_contact_value: e.target.value })} className={inputCls} placeholder="handle / number / email" />
            </Field>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {site.messenger_url && <a href={site.messenger_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"><MessageCircle size={13} /> Open Messenger</a>}
            {site.facebook_url && <a href={site.facebook_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"><Facebook size={13} /> Open Facebook</a>}
            {site.instagram_url && <a href={site.instagram_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"><Instagram size={13} /> Open Instagram</a>}
            {site.phone && <a href={`tel:${site.phone}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"><Phone size={13} /> Call {site.phone}</a>}
            {site.email && <a href={`mailto:${site.email}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"><Mail size={13} /> Email</a>}
            {site.existing_website_url && <a href={site.existing_website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"><Globe size={13} /> Their Existing Site</a>}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">These buttons only open the channel in a new tab or your device's dialer/mail app — nothing is ever sent automatically.</p>

          <div className="mt-5 grid sm:grid-cols-3 gap-4">
            <Field label="Recommended plan">
              <select value={form.recommended_plan} onChange={(e) => setForm({ ...form, recommended_plan: e.target.value })} className={inputCls}>
                <option value="">Not set</option>
                {Object.entries(PLAN_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Selected plan">
              <select value={form.selected_plan} onChange={(e) => setForm({ ...form, selected_plan: e.target.value })} className={inputCls}>
                <option value="">Not selected yet</option>
                {Object.entries(PLAN_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Sales priority">
              <select value={form.sales_priority} onChange={(e) => setForm({ ...form, sales_priority: e.target.value })} className={inputCls}>
                <option value="">Not set</option>
                <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Internal sales hook">
              <textarea value={form.sales_hook} onChange={(e) => setForm({ ...form, sales_hook: e.target.value })} rows={2} className={inputCls} placeholder="One sentence: why this prospect is worth contacting." />
            </Field>
          </div>

          <button type="button" disabled={saving} onClick={() => saveFields({
            primary_contact_channel: form.primary_contact_channel || null,
            primary_contact_value: form.primary_contact_value || null,
            decision_maker_name: form.decision_maker_name || null,
            decision_maker_role: form.decision_maker_role || null,
            decision_maker_verified: form.decision_maker_verified,
            recommended_plan: form.recommended_plan || null,
            selected_plan: form.selected_plan || null,
            sales_priority: form.sales_priority || null,
            sales_hook: form.sales_hook || null,
          })} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><Save size={14} /> Save Contact &amp; Plan Info</button>
        </section>

        {/* Outreach Workspace */}
        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold">Outreach Workspace</h2>
          <p className="mt-1 text-xs text-slate-500">Prepared, editable message drafts. Nothing here is ever sent automatically — copy and send manually via the channel buttons above.</p>

          {[
            { key: "outreach_initial", label: "First Message" },
            { key: "outreach_followup_1", label: "Follow-Up 1" },
            { key: "outreach_followup_2", label: "Follow-Up 2" },
          ].map(({ key, label }) => (
            <div key={key} className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
                <CopyButton text={withLink(form[key])} label={`Copy ${label}`} />
              </div>
              <textarea value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} rows={4} className={inputCls} />
            </div>
          ))}

          <div className="mt-4 flex items-center gap-2">
            <CopyButton text={conceptUrl} label="Copy Concept Link" />
            <button type="button" disabled={saving} onClick={() => saveFields({
              outreach_initial: form.outreach_initial || null,
              outreach_followup_1: form.outreach_followup_1 || null,
              outreach_followup_2: form.outreach_followup_2 || null,
            })} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><Save size={14} /> Save Outreach Drafts</button>
          </div>
        </section>

        {/* Follow-up */}
        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold">Follow-Up</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {[["Tomorrow", 1], ["3 days", 3], ["7 days", 7], ["14 days", 14]].map(([label, days]) => (
              <button key={label} type="button" onClick={() => applyFollowUpShortcut(days)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">{label}</button>
            ))}
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            <Field label="Follow-up date">
              <input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Follow-up note">
              <input value={followUpNote} onChange={(e) => setFollowUpNote(e.target.value)} className={inputCls} placeholder="Follow up after owner returns from Cebu." />
            </Field>
          </div>
          <button type="button" disabled={saving} onClick={() => saveFields({ next_follow_up_at: followUpDate || null, follow_up_note: followUpNote || null })} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><Save size={14} /> Save Follow-Up</button>
        </section>

        {/* Objection */}
        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold">Objection</h2>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            <Field label="Category">
              <select value={objectionCategory} onChange={(e) => setObjectionCategory(e.target.value)} className={inputCls}>
                <option value="">None recorded</option>
                {Object.entries(OBJECTION_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Exact wording (preserve verbatim)">
              <input value={objectionText} onChange={(e) => setObjectionText(e.target.value)} className={inputCls} placeholder={'"Why would I pay 7,500 every month just for a website?"'} />
            </Field>
          </div>
          <button type="button" disabled={saving} onClick={() => saveFields({ objection_category: objectionCategory || null, objection_text: objectionText || null })} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><Save size={14} /> Save Objection</button>
        </section>

        {/* Sales notes */}
        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold">Internal Sales Notes</h2>
          <textarea value={form.sales_notes} onChange={(e) => setForm({ ...form, sales_notes: e.target.value })} rows={3} className={`${inputCls} mt-3`} />
          <button type="button" disabled={saving} onClick={() => saveFields({ sales_notes: form.sales_notes || null })} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><Save size={14} /> Save Notes</button>
        </section>

        {/* Contact activity history */}
        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold">Contact History</h2>

          <form onSubmit={submitActivity} className="mt-4 grid sm:grid-cols-2 gap-3 rounded-lg border border-slate-100 bg-slate-50 p-4">
            <Field label="Direction">
              <select value={activityForm.direction} onChange={(e) => setActivityForm({ ...activityForm, direction: e.target.value })} className={inputCls}>
                <option value="outbound">Outbound</option><option value="inbound">Inbound</option><option value="internal">Internal</option>
              </select>
            </Field>
            <Field label="Activity type">
              <select value={activityForm.activity_type} onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })} className={inputCls}>
                {Object.entries(ACTIVITY_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Channel">
              <select value={activityForm.channel} onChange={(e) => setActivityForm({ ...activityForm, channel: e.target.value })} className={inputCls}>
                <option value="">Not set</option>
                {Object.entries(CHANNEL_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Result (optional)">
              <input value={activityForm.result} onChange={(e) => setActivityForm({ ...activityForm, result: e.target.value })} className={inputCls} placeholder="e.g. no answer, asked to call back" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Summary">
                <input value={activityForm.summary} onChange={(e) => setActivityForm({ ...activityForm, summary: e.target.value })} className={inputCls} placeholder="Short description of what happened" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Exact message / response text (optional)">
                <textarea value={activityForm.message_text} onChange={(e) => setActivityForm({ ...activityForm, message_text: e.target.value })} rows={2} className={inputCls} />
              </Field>
            </div>
            <button type="submit" disabled={saving} className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><Plus size={14} /> Log Activity</button>
          </form>

          <ul className="mt-5 space-y-3">
            {record.activity.map((a) => (
              <li key={a.id} className="rounded-lg border border-slate-100 p-3 text-sm">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{new Date(a.at).toLocaleString()} · {a.admin_email}</span>
                  <span className="uppercase tracking-wide">{a.direction}</span>
                </div>
                <p className="mt-1 font-semibold">{ACTIVITY_TYPE_LABEL[a.activity_type] || a.activity_type}{a.channel ? ` · ${CHANNEL_LABEL[a.channel] || a.channel}` : ""}</p>
                {a.summary && <p className="mt-1 text-slate-600">{a.summary}</p>}
                {a.message_text && <p className="mt-1 text-slate-500 italic">"{a.message_text}"</p>}
                {a.result && <p className="mt-1 text-slate-500">Result: {a.result}</p>}
              </li>
            ))}
            {record.activity.length === 0 && <p className="text-sm text-slate-400">No contact activity logged yet.</p>}
          </ul>
        </section>

        {/* Audit history */}
        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold">Sales History (Audit Trail)</h2>
          <ul className="mt-3 space-y-2">
            {record.history.map((h) => (
              <li key={h.id} className="text-xs text-slate-500">
                {new Date(h.at).toLocaleString()} — {h.admin_email} changed <strong>{h.field}</strong> from "{String(h.previous_value)}" to "{String(h.new_value)}"{h.note ? ` (${h.note})` : ""}
              </li>
            ))}
            {record.history.length === 0 && <p className="text-sm text-slate-400">No sales-field changes recorded yet.</p>}
          </ul>
        </section>
      </div>
    </div>
  );
}
